/**
 * The monitoring dashboard's Settings tab (DOP-480).
 *
 * What the template IS, and the one thing about it a user can change today.
 *
 * The template PATCH accepts exactly two things (`MonitoringUpdateTemplatePayload`):
 * the missing-figure policy and the per-metric include flags. The flags belong
 * next to the metrics they switch, so they stay on Data → Metrics; the policy
 * is a setting and lives here. Name, fund and cadence have no PATCH field at
 * all, so they are shown as the facts they are rather than as inputs that
 * would refuse to save (epic decision 4: a Settings tab is read-only wherever
 * the API has no field for it).
 *
 * The borrower roster is not repeated here: adding a borrower is done where
 * the roster is read, on Data → Borrowers and on the period board.
 */

import React from "react";
import type {
	MonitoringMissingDataPolicy,
	MonitoringTemplate,
} from "../../types/monitoring";
import MissingDataPolicyCard from "./MissingDataPolicyCard";
import MonitoringSettingsCard from "./MonitoringSettingsCard";

interface KeyValueRowProps {
	label: string;
	value: string;
}

const KeyValueRow: React.FC<KeyValueRowProps> = ({ label, value }) => (
	<div className="flex items-baseline justify-between gap-3 border-t border-[#f1f2f4] py-2 first:border-0">
		<span className="text-[13px] text-[#6B7280]">{label}</span>
		<span className="text-[13px] text-[#111111]">{value}</span>
	</div>
);

export interface TemplateSettingsViewProps {
	template: MonitoringTemplate;
	onChangePolicy: (policy: MonitoringMissingDataPolicy) => void;
	/** A policy save is in flight: the radios are frozen until it lands. */
	savingPolicy?: boolean;
}

const TemplateSettingsView: React.FC<TemplateSettingsViewProps> = ({
	template,
	onChangePolicy,
	savingPolicy = false,
}) => (
	<div className="grid gap-4">
		<MonitoringSettingsCard title="Template">
			<KeyValueRow label="Name" value={template.name} />
			<KeyValueRow label="Fund" value={template.fund_name} />
		</MonitoringSettingsCard>

		<MonitoringSettingsCard title="Cadence & review">
			<KeyValueRow label="Runs" value={template.cadence} />
			<KeyValueRow
				label="Package due"
				value={`${template.package_due_days} days after period end`}
			/>
			<KeyValueRow
				label="Internal deadline"
				value={`${template.internal_deadline_days} days after the package`}
			/>
			<KeyValueRow label="Lock rule" value="Locked runs cannot be edited" />
			<KeyValueRow
				label="Reviewer"
				value={template.reviewer_name ?? "Not assigned"}
			/>
		</MonitoringSettingsCard>

		<MissingDataPolicyCard
			policy={template.missing_data_policy}
			onChange={onChangePolicy}
			saving={savingPolicy}
		/>
	</div>
);

export default TemplateSettingsView;
